import React from 'react'
import { useTeamContext } from '@/context/TeamContext'
import { useProgramContext } from '@/context/ProgramContext'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Loader2, AlertTriangle, RefreshCcw } from 'lucide-react'
import { useErrorRecovery } from '@/hooks/useErrorRecovery'

interface DataLoadingGuardProps {
	children: React.ReactNode
	requireProgram?: boolean
}

export function DataLoadingGuard({
	children,
	requireProgram = true,
}: DataLoadingGuardProps) {
	const {
		isInitialized: teamInitialized,
		isLoading: teamLoading,
		error: teamError,
		refetch: refetchTeam,
		teamId,
	} = useTeamContext()

	const {
		loading: programLoading,
		error: programError,
		currentProgram,
	} = useProgramContext()

	const { retry: retryError, isRetrying } = useErrorRecovery()

	// Handle retry
	const handleRetry = async () => {
		await retryError([['team'], ['programs']], 'Reloading application data...')
		await refetchTeam()
	}

	// Check loading state
	if (!teamInitialized || (requireProgram && programLoading)) {
		return (
			<div className="flex items-center justify-center min-h-[400px]">
				<div className="flex flex-col items-center gap-4">
					<div className="relative">
						<div className="w-12 h-12 border-4 border-muted rounded-full" />
						<Loader2 className="absolute inset-0 w-12 h-12 animate-spin text-primary" />
					</div>
					<p className="text-sm text-muted-foreground animate-pulse">
						Loading application data...
					</p>
				</div>
			</div>
		)
	}

	// Check for errors
	if (teamError || (requireProgram && programError)) {
		return (
			<div className="max-w-4xl mx-auto p-6">
				<Alert variant="destructive">
					<AlertTriangle className="h-4 w-4" />
					<AlertTitle>Unable to Load Data</AlertTitle>
					<AlertDescription className="flex flex-col gap-2">
						<span>
							{teamError?.message ||
								programError?.message ||
								'Failed to load required data'}
						</span>
						<Button
							variant="outline"
							size="sm"
							onClick={handleRetry}
							disabled={isRetrying}
							className="w-fit mt-2">
							{isRetrying ? (
								<>
									<Loader2 className="h-3 w-3 mr-1 animate-spin" />
									Retrying...
								</>
							) : (
								<>
									<RefreshCcw className="h-3 w-3 mr-1" />
									Retry
								</>
							)}
						</Button>
					</AlertDescription>
				</Alert>
			</div>
		)
	}

	// Check if team exists
	if (!teamId) {
		return (
			<div className="max-w-4xl mx-auto p-6">
				<Alert>
					<AlertTriangle className="h-4 w-4" />
					<AlertTitle>No Team Found</AlertTitle>
					<AlertDescription>
						You need to be part of a team to access this area. Please contact
						your administrator.
					</AlertDescription>
				</Alert>
			</div>
		)
	}

	// Check if program exists (if required)
	if (requireProgram && !currentProgram) {
		return (
			<div className="max-w-4xl mx-auto p-6">
				<Alert>
					<AlertTriangle className="h-4 w-4" />
					<AlertTitle>No Program Selected</AlertTitle>
					<AlertDescription>
						Please select or create a program to continue.
					</AlertDescription>
				</Alert>
			</div>
		)
	}

	// All checks passed, render children
	return <>{children}</>
}
